import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Button,
    Drawer,
    SimpleGrid,
    Stack,
    Text,
    UnstyledButton,
    useComputedColorScheme,
    useMantineColorScheme,
} from '@mantine/core';
import { ExternalLink, LayoutGrid, LogOut, Moon, Sun } from 'lucide-react';
import { clearToken } from '@/auth/session';
import { isNavActive, NAV_LEGACY, NAV_MAIN, type NavItem } from '@/navigation';

const TABS = NAV_MAIN.filter((i) => i.tab);
const MORE = NAV_MAIN.filter((i) => !i.tab);

function Tile({ item, onClick }: { item: NavItem; onClick: () => void }) {
    const content = (
        <>
            <item.icon size={22} />
            <Text size="xs" fw={600} ta="center" lh={1.2}>
                {item.label}
            </Text>
        </>
    );
    if (item.href) {
        return (
            <UnstyledButton component="a" href={item.href} target="_blank" rel="noopener" onClick={onClick} className="ff-more-tile">
                {content}
                <ExternalLink size={12} className="ff-more-tile-ext" />
            </UnstyledButton>
        );
    }
    return (
        <UnstyledButton component={Link} to={item.to!} onClick={onClick} className="ff-more-tile">
            {content}
        </UnstyledButton>
    );
}

function MoreSheet({ opened, onClose }: { opened: boolean; onClose: () => void }) {
    const { setColorScheme } = useMantineColorScheme();
    const scheme = useComputedColorScheme('dark');
    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="bottom"
            title="Ещё"
            radius="lg"
            styles={{ content: { height: 'auto', maxHeight: '90dvh' } }}
        >
            <Stack pb="md">
                <SimpleGrid cols={3} spacing="sm">
                    {MORE.map((item) => (
                        <Tile key={item.label} item={item} onClick={onClose} />
                    ))}
                </SimpleGrid>
                <Text size="xs" c="dimmed" fw={600} tt="uppercase" mt="xs">
                    В старой админке
                </Text>
                <SimpleGrid cols={3} spacing="sm">
                    {NAV_LEGACY.map((item) => (
                        <Tile key={item.label} item={item} onClick={onClose} />
                    ))}
                </SimpleGrid>
                <SimpleGrid cols={2} spacing="sm" mt="xs">
                    <Button
                        variant="default"
                        size="md"
                        leftSection={scheme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                        onClick={() => setColorScheme(scheme === 'dark' ? 'light' : 'dark')}
                    >
                        {scheme === 'dark' ? 'Светлая' : 'Тёмная'}
                    </Button>
                    <Button variant="light" color="red" size="md" leftSection={<LogOut size={18} />} onClick={clearToken}>
                        Выйти
                    </Button>
                </SimpleGrid>
            </Stack>
        </Drawer>
    );
}

/**
 * Нижний таббар на телефоне — навигация под большой палец. На ≥48em скрыт CSS'ом,
 * там работает боковое меню. Пятый слот «Ещё» открывает шторку с остальными разделами.
 */
export function TabBar() {
    const { pathname } = useLocation();
    const [more, setMore] = useState(false);

    useEffect(() => setMore(false), [pathname]);

    const moreActive = MORE.some((i) => isNavActive(pathname, i.to!));

    return (
        <>
            <nav className="ff-tabbar" aria-label="Разделы">
                {TABS.map((item) => {
                    const active = isNavActive(pathname, item.to!);
                    return (
                        <Link
                            key={item.label}
                            to={item.to!}
                            className="ff-tab"
                            data-active={active || undefined}
                            aria-current={active ? 'page' : undefined}
                        >
                            <span className="ff-tab-icon">
                                <item.icon size={22} />
                            </span>
                            <span className="ff-tab-label">{item.short ?? item.label}</span>
                        </Link>
                    );
                })}
                <button
                    type="button"
                    className="ff-tab"
                    data-active={moreActive || more || undefined}
                    onClick={() => setMore(true)}
                >
                    <span className="ff-tab-icon">
                        <LayoutGrid size={22} />
                    </span>
                    <span className="ff-tab-label">Ещё</span>
                </button>
            </nav>
            <MoreSheet opened={more} onClose={() => setMore(false)} />
        </>
    );
}
